import * as S from './styles'
import { useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'store/store'
import { Controller, useForm } from 'react-hook-form'

import {
  Icon,
  Input,
  Label,
  OptionProps,
  SectionCard,
  Select,
  SlimButton,
  UploadButton
} from 'components'

import { getDetailedInfo, saveOvFields } from 'store/OVContext/ovSlice'

import { statusNegDiffApproval } from 'services/nd/constants'
import { ChangeEvent, useEffect, useRef, useState, useMemo } from 'react'
import { useElementPosition } from 'commons/customHooks/useElementPosition'
import { simpleId } from 'commons/utils/format'
import { base64toFile } from 'commons/utils/object'
import { formatDate } from 'commons/utils/date'
import { OVTypes } from 'constants/enums'
import { theme } from 'styles/theme/hav-theme'

export const NDSection = () => {
  const { ovNumber } = useParams()
  const dispatch = useDispatch()

  const {
    ovSelected: {
      Cabecalho: { CodigoStatus, CodigoTipoOrdem }
    },
    detailedInfo: { Tracking },
    observation,
    loadingObservation: loading,
    reasonsJustification,
    NDJustificationDescription
  } = useSelector((state) => state.ov)

  useEffect(() => {
    if (!Tracking) {
      dispatch(getDetailedInfo(ovNumber ?? ''))
    }
  }, [ovNumber])

  const {
    control,
    setValue,
    getValues,
    reset,
    formState: { isDirty }
  } = useForm()

  const [keyFile, setKeyFile] = useState(simpleId())

  const yrbaCode = '12'
  const isYRBA = useMemo(() => CodigoTipoOrdem === OVTypes.YRBA, [CodigoTipoOrdem])

  const defaultValues = {
    file: observation?.Arquivo
      ? base64toFile(observation?.Arquivo, observation?.NomeArquivo ?? '')
      : new File([], ''),
    file_name: observation?.NomeArquivo ?? '',
    codReason: isYRBA ? yrbaCode : observation?.CodigoMotivoNegociacaoDiferenciada ?? '',
    descReason: observation?.DescricaoMotivoNegociacaoDiferenciada ?? '',
    requestDescription: observation?.Justificativa ?? ''
  }

  useEffect(() => {
    if (isYRBA) {
      const yrbaReason = reasonsOption().find((item) => item.value === yrbaCode)
      reset({
        ...defaultValues,
        requestDescription: NDJustificationDescription,
        codeReason: yrbaCode,
        descReason: yrbaReason?.title as string
      })
    }
  }, [NDJustificationDescription])

  useEffect(() => {
    if (observation?.Arquivo && observation?.NomeArquivo) {
      reset(defaultValues)
    }
  }, [observation?.Arquivo])

  useEffect(() => {
    if (isYRBA) {
      const yrbaReason = reasonsOption().find((item) => item.value === yrbaCode)
      setValue('codReason', yrbaCode)
      setValue('descReason', yrbaReason?.title as string)
    }
  }, [CodigoTipoOrdem])

  const flagNd = observation.StatusNegociacaoDiferenciadaAprovacao
  const motivo = observation.MotivoReprovacao

  const selectRef = useRef<HTMLDivElement>(null)
  const reasonsMenuPosition = useElementPosition(selectRef)

  const reasonsOption = (): OptionProps[] =>
    reasonsJustification &&
    reasonsJustification
      .filter((item) => {
        if (CodigoTipoOrdem !== OVTypes.YRBA) {
          return (
            item.CodigoMotivoNegociacao !== '15' && item.CodigoMotivoNegociacao !== '12'
          )
        }
        return true
      })
      .map((reason) => ({
        value: reason.CodigoMotivoNegociacao,
        title: reason.DescricaoMotivoNegociacao
      }))

  const returnNDStatus = (): JSX.Element | null => {
    const statusNd: Record<string, JSX.Element> = {
      [statusNegDiffApproval.backOffice]: (
        <>
          <Icon icon="clock" color={theme.colors.neutral.mediumhover} size="18" />
          <span>Pendente</span>
        </>
      ),
      [statusNegDiffApproval.directors]: (
        <>
          <Icon icon="task-alt" color={theme.colors.alert.success.dark} size="18" />
          <span>Aprovada</span>
        </>
      ),
      [statusNegDiffApproval.reject]: (
        <>
          <Icon icon="report" color={theme.colors.alert.danger.darker} size="18" />
          <span>Reprovada</span>
        </>
      ),
      [statusNegDiffApproval.pending]: (
        <>
          <Icon icon="clock" color={theme.colors.neutral.mediumhover} size="18" />
          <span>Pendente</span>
        </>
      )
    }

    return flagNd && statusNd[flagNd]
  }

  const handleSaveOV = () => {
    const data = getValues()
    data.descReason =
      reasonsOption().find((r) => r.value === data.codReason)?.title ?? undefined

    dispatch(
      saveOvFields(
        data.file,
        data.file_name,
        data.descReason,
        data.codReason,
        data.requestDescription
      )
    )
    reset({ ...data })
  }
  return (
    <S.ColumnWrapper>
      <SectionCard
        title="Negociação diferenciada"
        tooltipText="Esta é uma seção obrigatória exibida a partir do registro de desconto, condição de pagamento ou prazo extra superior ao permitido pelas condições comerciais do cliente vinculado a esta OV. Além de acessar a justificativa da OV,  você também pode acompanhar o status de análise e, caso reprovada, o motivo da reprovação da ND."
      >
        <S.Wrapper>
          <S.Form>
            {(flagNd === statusNegDiffApproval.reject ||
              flagNd === statusNegDiffApproval.directors) && (
              <div className="row no-gap">
                <div className="infoRow2 firstColumn">
                  <div className="infoColumn">
                    <Label>Status</Label>
                    <div className="info">{returnNDStatus()}</div>
                  </div>
                  <div className="infoColumn">
                    <Label>Última atualização</Label>
                    <div className="info">
                      {formatDate(Tracking?.DataUltimaAlteracao)}
                    </div>
                  </div>
                </div>
                {flagNd === 'R' && (
                  <div className="infoRow expand">
                    <div className="infoColumn">
                      <Label>Motivo de reprovação</Label>
                      <div className="info">{motivo}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="row">
              <S.FormField className="firstColumn">
                <div ref={selectRef} className="selectContainer">
                  <Controller
                    name={'codReason'}
                    control={control}
                    defaultValue={observation?.CodigoMotivoNegociacaoDiferenciada ?? ''}
                    render={({ field: { name, onChange, value } }) => (
                      <Select
                        label="Motivo da solicitação"
                        name={name}
                        value={value}
                        options={reasonsOption()}
                        onChange={(e) => {
                          const reason = reasonsOption().find(
                            (r) => r.value === e.target.value
                          )
                          reason && setValue('descReason', reason?.title)

                          onChange(e.target.value)
                        }}
                        optionsMaxHeight={180}
                        disabled={
                          +CodigoStatus !== 0 ||
                          !reasonsJustification.length ||
                          CodigoTipoOrdem === OVTypes.YRBA
                        }
                        optionsMaxWidth={reasonsMenuPosition}
                        required
                      />
                    )}
                  />
                </div>
              </S.FormField>
              <S.FormField className="firstColumn">
                <div className="description">
                  <Controller
                    name="requestDescription"
                    control={control}
                    defaultValue={observation?.Justificativa ?? ''}
                    render={({ field: { onChange, value } }) => (
                      <Input
                        value={value}
                        onChange={onChange}
                        label="Descrição da solicitação"
                        disabled={+CodigoStatus !== 0}
                        height={132}
                        rows={5}
                        InputProps={{
                          placeholder: 'Digite aqui mais informações sobre a solicitação'
                        }}
                        maxLength={264}
                        required
                        showMaxLength
                      />
                    )}
                  />
                </div>
              </S.FormField>
              <S.FormField className="firstColumn">
                <div>
                  <Controller
                    name="file"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <UploadButton
                        key={`fileInput-${keyFile}`}
                        value={value as File}
                        disabled={+CodigoStatus !== 0}
                        onClear={() => {
                          setKeyFile(simpleId())
                          setValue('file', new File([], ''))
                          setValue('file_name', '', { shouldDirty: true })
                        }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          if (e.target.files?.length) {
                            onChange(e.target.files[0])
                            setValue(`file_name`, e.target.files[0].name, {
                              shouldDirty: true
                            })
                          }
                        }}
                        id="fileInput"
                        name="upload"
                        placeholder="Clique aqui para selecionar arquivo"
                        required={isYRBA}
                      />
                    )}
                  />
                </div>
              </S.FormField>
            </div>
            {isDirty && (
              <S.FormActions>
                <SlimButton
                  content="Salvar"
                  type="button"
                  variant="primary"
                  loading={loading}
                  onClick={() => handleSaveOV()}
                />
                <SlimButton
                  content="Desfazer"
                  type="button"
                  variant="secondary"
                  disabled={loading}
                  onClick={() => reset(defaultValues)}
                />
              </S.FormActions>
            )}
          </S.Form>
        </S.Wrapper>
      </SectionCard>
    </S.ColumnWrapper>
  )
}
